import type {
  EnergyConsumerObject,
  GridElementIdentity,
  LoadFlowScenarioElementCalculation,
  LoadFlowScenarioResults
} from '../../../..';
import type {
  EnergyConsumerEvaluatedVoltageCosinePhi,
  EnergyConsumerEvaluatedVoltageDiffTransformerEnd,
  EnergyConsumerEvaluatedVoltageDiffTransformerEndPercent,
  EnergyConsumerEvaluatedVoltages
} from '../../../../derived-state/loadFlowSummary';
import type { Degree } from '../../../../domain/Degree';
import type { Percent } from '../../../../domain/Percent';
import type { Voltage } from '../../../../domain/Voltage';
import type { ForStoringAttentionStatus } from '../../../AttentionStatusRepository';
import type { EvaluatedResult } from '../../../EvaluatedResult';
import {
  selectByType,
  selectByTypeFromAllScenarios
} from '../../../selectByType';
import { EnergyConsumerSummaryEvaluation } from './energyConsumerSummaryEvaluation';
import type { ForEnergyConsumerEvaluation } from './forEnergyConsumerEvaluation';

export class EnergyConsumerEvaluation {
  private constructor(
    private readonly energyConsumers: EnergyConsumerObject[],
    private readonly evaluationStatusRepository: ForStoringAttentionStatus
  ) {}

  static from(
    loadFlowScenarios: LoadFlowScenarioResults[],
    evaluationStatusRepository: ForStoringAttentionStatus
  ) {
    const results = selectByTypeFromAllScenarios(
      'EnergyConsumer',
      loadFlowScenarios
    );
    return new EnergyConsumerEvaluation(results, evaluationStatusRepository);
  }

  static fromResults(
    loadFlowResults: LoadFlowScenarioElementCalculation[],
    evaluationStatusRepository: ForStoringAttentionStatus
  ) {
    const results = selectByType('EnergyConsumer', loadFlowResults);
    return new EnergyConsumerEvaluation(results, evaluationStatusRepository);
  }

  private get energyConsumerResults() {
    return this.energyConsumers.map(energyConsumer => energyConsumer.result);
  }

  evaluateSummary(
    evaluationEngine: ForEnergyConsumerEvaluation
  ): EnergyConsumerEvaluationResult {
    const summaryEvaluation = new EnergyConsumerSummaryEvaluation(
      this.energyConsumerResults,
      evaluationEngine
    );
    const voltageDiffTransformerEndPercentResult =
      summaryEvaluation.evaluateVoltageDiffPercent();
    const voltageDiffTransformerEndResult =
      summaryEvaluation.evaluateVoltageDiffTransformerEnd();
    const voltageResult = summaryEvaluation.evaluateVoltages();
    const voltageCosinePhiResult = summaryEvaluation.evaluateVoltageCosinePhi();
    const voltageNominalResult = summaryEvaluation.evaluateVoltageDiffNominal();

    const attentionRequired = this.isAttentionRequired(
      voltageDiffTransformerEndPercentResult,
      voltageDiffTransformerEndResult,
      voltageResult,
      voltageCosinePhiResult,
      voltageNominalResult
    );

    return {
      voltageDiffTransformerEndPercentResult,
      voltageDiffTransformerEndResult,
      voltageResult,
      voltageCosinePhiResult,
      voltageNominalResult,
      attentionRequired
    };
  }

  evaluateEachConsumer(
    evaluationEngine: ForEnergyConsumerEvaluation
  ): EvaluatedEnergyConsumer[] {
    return this.energyConsumers.map(energyConsumer => {
      return this.evaluateEnergyConsumer(evaluationEngine, energyConsumer);
    });
  }

  private evaluateEnergyConsumer(
    evaluationEngine: ForEnergyConsumerEvaluation,
    energyConsumer: EnergyConsumerObject
  ): EvaluatedEnergyConsumer {
    const messenger = this.evaluationStatusRepository.delegateMessenger(
      energyConsumer.id,
      'EnergyConsumer'
    );

    const voltage = evaluationEngine.evaluateVoltage(
      energyConsumer.result.voltage,
      messenger
    );

    const voltageDiffTransformerEndPercentValue =
      energyConsumer.result.voltageDiffTransformerEndPercent !== undefined
        ? Math.abs(energyConsumer.result.voltageDiffTransformerEndPercent)
        : energyConsumer.result.voltageDiffTransformerEndPercent;
    const voltageDiffPercent =
      evaluationEngine.evaluateVoltageDiffTransformerEndPercent(
        voltageDiffTransformerEndPercentValue,
        messenger
      );
    const voltageCosinePhi = evaluationEngine.evaluateVoltageCosinePhi(
      energyConsumer.result.voltageCosinePhi,
      messenger
    );
    const voltageDiffNominal = evaluationEngine.evaluateVoltageDiffNominal(
      energyConsumer.result.voltageDiffNominal,
      messenger
    );
    const voltageDiffTransformer =
      evaluationEngine.evaluateVoltageDiffTransformerEnd(
        energyConsumer.result.voltageDiffTransformerEnd,
        messenger
      );

    return {
      id: energyConsumer.id,
      objectType: energyConsumer.objectType,
      results: {
        voltage,
        voltageDiffPercent,
        voltageCosinePhi,
        voltageDiffNominal,
        voltageDiffTransformer
      }
    };
  }

  isAttentionRequired(
    voltageDiffTransformerEndPercentResult: EnergyConsumerEvaluatedVoltageDiffTransformerEndPercent,
    voltageDiffTransformerEndResult: EnergyConsumerEvaluatedVoltageDiffTransformerEnd,
    voltageResult: EnergyConsumerEvaluatedVoltages,
    voltageCosinePhiResult: EnergyConsumerEvaluatedVoltageCosinePhi,
    voltageNominalResult: EnergyConsumerEvaluatedVoltageDiffTransformerEnd
  ) {
    return (
      voltageDiffTransformerEndPercentResult.min.status ===
        'ATTENTION_REQUIRED' ||
      voltageDiffTransformerEndPercentResult.max.status ===
        'ATTENTION_REQUIRED' ||
      voltageDiffTransformerEndResult.min.status === 'ATTENTION_REQUIRED' ||
      voltageDiffTransformerEndResult.max.status === 'ATTENTION_REQUIRED' ||
      voltageResult.min.status === 'ATTENTION_REQUIRED' ||
      voltageResult.max.status === 'ATTENTION_REQUIRED' ||
      voltageCosinePhiResult.min.status === 'ATTENTION_REQUIRED' ||
      voltageCosinePhiResult.max.status === 'ATTENTION_REQUIRED' ||
      voltageNominalResult.min.status === 'ATTENTION_REQUIRED' ||
      voltageNominalResult.max.status === 'ATTENTION_REQUIRED'
    );
  }
}

export type EnergyConsumerEvaluationResult = {
  voltageDiffTransformerEndPercentResult: EnergyConsumerEvaluatedVoltageDiffTransformerEndPercent;
  voltageDiffTransformerEndResult: EnergyConsumerEvaluatedVoltageDiffTransformerEnd;
  voltageResult: EnergyConsumerEvaluatedVoltages;
  voltageCosinePhiResult: EnergyConsumerEvaluatedVoltageCosinePhi;
  voltageNominalResult: EnergyConsumerEvaluatedVoltageDiffTransformerEnd;
  attentionRequired: boolean;
};

export type EvaluatedEnergyConsumer = GridElementIdentity & {
  objectType: 'EnergyConsumer';
  results: {
    voltage: EvaluatedResult<Voltage>;
    voltageDiffPercent: EvaluatedResult<Percent>;
    voltageCosinePhi: EvaluatedResult<Degree>;
    voltageDiffNominal: EvaluatedResult<Voltage>;
    voltageDiffTransformer: EvaluatedResult<Voltage>;
  };
};
